/**
 * quoteWarranty — a garantia do orçamento, com o texto legado INTACTO.
 *
 * ─── A REGRA QUE DEFINE ESTA FEATURE ────────────────────────────────────────
 *
 * `quotes.warranty` é texto livre desde sempre, e continua sendo. As colunas
 * novas (`warranty_amount` + `warranty_unit`) são um SEGUNDO estado, não uma
 * substituição — exatamente como o QR PIX do #73, e pela mesma razão: um
 * documento antigo não pode mudar de aparência porque uma coluna nasceu.
 *
 * **NADA É CONVERTIDO AUTOMATICAMENTE.** É a lição da armadilha #64 (os dois
 * vocabulários de unidade): "corrigir" um dado de texto sem certeza da intenção
 * corrompe um dos grupos em silêncio. "90 dias" e "3 meses" caberiam em
 * número+unidade; **"1 ano na peça, 90 dias na mão de obra" NÃO CABE** — e é
 * justamente nesse caso que o texto carrega a informação que importa. Um
 * parser em massa transformaria essa frase em "1 ano" e ninguém veria o erro.
 *
 * O que o app faz, no lugar de adivinhar: `suggestWarranty` reconhece SÓ as
 * frases inequívocas e oferece a conversão na edição, para o usuário
 * **confirmar com um clique**. Sem clique, o texto fica como está, para sempre.
 *
 * ─── PRECEDÊNCIA DE EXIBIÇÃO ────────────────────────────────────────────────
 *
 * Estruturada, se houver; senão o texto livre; senão nada. Isso mantém todo
 * documento existente idêntico ao que era (nenhum deles tem estrutura) e faz
 * o novo campo valer assim que o usuário o preenche.
 */

export const WARRANTY_UNITS = [
  { value: 'days', label: 'dias', singular: 'dia' },
  { value: 'months', label: 'meses', singular: 'mês' },
  { value: 'years', label: 'anos', singular: 'ano' },
] as const;

export type WarrantyUnit = (typeof WARRANTY_UNITS)[number]['value'];

export interface QuoteWarranty {
  /** Texto livre — o campo histórico. */
  warranty: string;
  /** Número da garantia estruturada. `null` = não informada (NUNCA zero). */
  warrantyAmount: number | null;
  warrantyUnit: WarrantyUnit | null;
}

function toUnit(value: unknown): WarrantyUnit | null {
  return WARRANTY_UNITS.some((unit) => unit.value === value) ? (value as WarrantyUnit) : null;
}

/**
 * Normaliza o par. Os dois andam JUNTOS (o banco tem CHECK para isso): número
 * sem unidade não é exibível, e meio par gravado seria um estado que nenhuma
 * tela sabe mostrar.
 */
export function normalizeWarranty(
  amount: number | string | null | undefined,
  unit: unknown,
): { warrantyAmount: number | null; warrantyUnit: WarrantyUnit | null } {
  const parsed = typeof amount === 'string' ? Number(amount.replace(',', '.')) : Number(amount);
  const validUnit = toUnit(unit);
  // Ausência é `null`, não zero (#61): garantia "0 dias" não existe.
  if (!Number.isFinite(parsed) || parsed <= 0 || !validUnit) {
    return { warrantyAmount: null, warrantyUnit: null };
  }
  return { warrantyAmount: parsed, warrantyUnit: validUnit };
}

/** Leitor: da linha do banco (ou do cache) para o formato da aplicação. */
export function quoteWarrantyFromRow(row: Record<string, any> | null | undefined): QuoteWarranty {
  const { warrantyAmount, warrantyUnit } = normalizeWarranty(
    row?.warranty_amount,
    row?.warranty_unit,
  );
  return { warranty: row?.warranty ?? '', warrantyAmount, warrantyUnit };
}

/**
 * Gravador: as TRÊS colunas de uma vez.
 *
 * Espalhe (`...buildQuoteWarrantyColumns(data)`) em vez de escrever
 * `warranty:` à mão — são 15 payloads de `quotes` no app, e a armadilha
 * #33/#62 é exatamente esta: um deles fica para trás, o save dá certo, e a
 * garantia some sem erro nenhum.
 */
export function buildQuoteWarrantyColumns(
  info: Partial<QuoteWarranty> | null | undefined,
): { warranty: string | null; warranty_amount: number | null; warranty_unit: string | null } {
  const { warrantyAmount, warrantyUnit } = normalizeWarranty(
    info?.warrantyAmount,
    info?.warrantyUnit,
  );
  return {
    warranty: info?.warranty?.trim() ? info.warranty : null,
    warranty_amount: warrantyAmount,
    warranty_unit: warrantyUnit,
  };
}

/** "90 dias", "1 mês", "2 anos" — plural correto (CLAUDE.md #87). */
export function formatStructuredWarranty(
  amount: number | null | undefined,
  unit: WarrantyUnit | null | undefined,
): string {
  const normalized = normalizeWarranty(amount, unit);
  if (normalized.warrantyAmount == null || !normalized.warrantyUnit) return '';
  const config = WARRANTY_UNITS.find((u) => u.value === normalized.warrantyUnit)!;
  const label = normalized.warrantyAmount === 1 ? config.singular : config.label;
  const number = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(
    normalized.warrantyAmount,
  );
  return `${number} ${label}`;
}

/**
 * O que sai no PDF e nas telas: estruturada se houver, senão o texto legado.
 *
 * Documento antigo (sem estrutura) devolve exatamente o texto que sempre
 * devolveu — é o que mantém os PDFs já enviados idênticos.
 */
export function warrantyDisplayText(
  info: Partial<QuoteWarranty> | null | undefined,
): string {
  const structured = formatStructuredWarranty(info?.warrantyAmount, info?.warrantyUnit);
  if (structured) return structured;
  return info?.warranty?.trim() ?? '';
}

/**
 * SUGESTÃO de estruturação a partir do texto livre — nunca aplicada sozinha.
 *
 * Reconhece só o que é inequívoco: um número, uma unidade, e mais nada. Frases
 * compostas ("1 ano na peça, 90 dias na mão de obra"), intervalos e qualquer
 * texto com informação extra devolvem `null` — porque estruturar esses casos
 * significaria JOGAR FORA parte do que o usuário escreveu.
 *
 * O retorno é uma proposta para a tela oferecer; quem converte é o clique do
 * usuário.
 */
export function suggestWarranty(
  text: string | null | undefined,
): { warrantyAmount: number; warrantyUnit: WarrantyUnit } | null {
  const raw = (text ?? '').trim().toLocaleLowerCase('pt-BR');
  if (!raw) return null;

  const match = raw.match(/^(\d{1,4})\s*(dia|dias|mes|mês|meses|ano|anos)$/);
  if (!match) return null;

  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const word = match[2];
  const unit: WarrantyUnit = word.startsWith('dia')
    ? 'days'
    : word.startsWith('ano')
      ? 'years'
      : 'months';

  return { warrantyAmount: amount, warrantyUnit: unit };
}
